import type { WorldTheme } from './theme';

/**
 * World 4 · SINALOA theme (GDD §10.6, §14.2, issue #125). The last postcard of the campaign: the brown Sierra
 * Madre at morning and afternoon, and a deep blue night crossed by white searchlights. Game art, not design
 * system tokens (GDD §14.1, §17-3); the definite values are recorded in GDD §14.2. The three variants are
 * `manana` (OP-401), `tarde` (OP-402) and `noche` (OP-403).
 *
 * Legibility (GDD §14.1): the collidable tints (columns and pickups) stay more saturated than every decoration
 * colour, so nothing that hurts ever looks like scenery. The dust red is kept in low-saturation earth tones so
 * it never reads as the single INTERCEPT red of the interface (GDD §13.1 rule 3).
 */

const DECORATION = {
  // Morning: pale brown sierra with hazy slopes.
  mananaFar: 0x8a7050,
  mananaFarSoft: 0x9a8060,
  mananaHill: 0x7f8a5a,
  mananaHillDark: 0x6b7548,
  mananaTown: 0xa06f42,
  mananaTownDark: 0x84573a,
  mananaForeground: 0x8a9a6a,
  // Afternoon: warmer brown sierra.
  tardeFar: 0x8a6a44,
  tardeFarSoft: 0x9a7a52,
  tardeHill: 0x74804f,
  tardeHillDark: 0x606a40,
  tardeTown: 0x96683e,
  tardeTownDark: 0x7a5034,
  tardeForeground: 0x7f8f5f,
  // Night: deep blue slopes, no warm tones, ready for the white searchlights.
  nocheFar: 0x2f2f45,
  nocheFarSoft: 0x3a3a52,
  nocheHill: 0x33445a,
  nocheHillDark: 0x28374a,
  nocheTown: 0x3f4a5a,
  nocheTownDark: 0x33404f,
  nocheForeground: 0x1f2a38,
} as const;

const layers = (palette: {
  far: readonly [number, number];
  hill: readonly [number, number];
  town: readonly [number, number];
  foreground: number;
}): WorldTheme['layers'] => [
  { name: 'layer.far', colors: palette.far, moduleWidth: 24, height: 9, baseY: 1, motif: 'hills' },
  { name: 'layer.mid', colors: [...palette.hill, ...palette.town], moduleWidth: 15, height: 5, baseY: 1, motif: 'buildings' },
  { name: 'layer.foreground', colors: [palette.foreground], moduleWidth: 10, height: 1.4, baseY: -0.6, motif: 'foreground', followCamera: true },
];

export const WORLD_4_MANANA: WorldTheme = {
  id: 'w4-sinaloa',
  variant: 'manana',
  sky: 0xb98a52,
  fog: { color: 0xb08050, near: 40, far: 150 },
  ground: 0x8a7050,
  ceiling: 0xe6d8c2,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xffe8c8, ground: 0x5a4a34, intensity: 1.0 },
    sun: { color: 0xffe6c2, intensity: 1.8, position: [4, 18, 12] },
  },
  layers: layers({
    far: [DECORATION.mananaFar, DECORATION.mananaFarSoft],
    hill: [DECORATION.mananaHill, DECORATION.mananaHillDark],
    town: [DECORATION.mananaTown, DECORATION.mananaTownDark],
    foreground: DECORATION.mananaForeground,
  }),
};

export const WORLD_4_TARDE: WorldTheme = {
  id: 'w4-sinaloa',
  variant: 'tarde',
  sky: 0xa87a48,
  fog: { color: 0x9a7040, near: 40, far: 150 },
  ground: 0x8a6a44,
  ceiling: 0xe0d0b8,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xffdcac, ground: 0x554428, intensity: 1.1 },
    sun: { color: 0xffd89a, intensity: 2.0, position: [-2, 16, 12] },
  },
  layers: layers({
    far: [DECORATION.tardeFar, DECORATION.tardeFarSoft],
    hill: [DECORATION.tardeHill, DECORATION.tardeHillDark],
    town: [DECORATION.tardeTown, DECORATION.tardeTownDark],
    foreground: DECORATION.tardeForeground,
  }),
};

export const WORLD_4_NOCHE: WorldTheme = {
  id: 'w4-sinaloa',
  variant: 'noche',
  sky: 0x1a2236,
  fog: { color: 0x141c2c, near: 40, far: 150 },
  ground: 0x2f2f45,
  ceiling: 0x9aa8c0,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    // Cold night with a hint of moonlight; the white searchlights are pure decoration.
    hemisphere: { sky: 0x8a9ac0, ground: 0x1a2030, intensity: 0.8 },
    sun: { color: 0xbfc8e0, intensity: 1.2, position: [-4, 16, 10] },
  },
  layers: layers({
    far: [DECORATION.nocheFar, DECORATION.nocheFarSoft],
    hill: [DECORATION.nocheHill, DECORATION.nocheHillDark],
    town: [DECORATION.nocheTown, DECORATION.nocheTownDark],
    foreground: DECORATION.nocheForeground,
  }),
};

export const WORLD_4: Readonly<Record<string, WorldTheme>> = {
  manana: WORLD_4_MANANA,
  tarde: WORLD_4_TARDE,
  noche: WORLD_4_NOCHE,
};
